import { Dispatch, SetStateAction } from "react";

import { ArrowFullLeftDarkIcon } from "../../../../assets";
import { PillData } from "../../../../models";
import {
  Button,
  DefaultKPI,
  Pill,
  ProfileMenuItemType,
  QuickActionsButton,
  ScrollablePills,
  ToggleTabs,
} from "../../../../widget";
import { DealStagePill } from "./DealStagePill";

const DEAL_STATUS_PILL_VARIANT_MAP = {
  Active: "coral",
  Approved: "green",
  Rejected: "purple",
  Cancelled: "red",
} as const;

export type DealStageItem = {
  key: string;
  label: string;
  status?: string | null;
};

export type DealSummaryKpiCard = {
  key: string;
  label: string;
  value: string;
  icon: JSX.Element;
};

export interface DealSummaryHeaderSectionProps {
  title: string;
  isLoading: boolean;
  pipelineTypeLabel?: string | null;
  statusLabel?: string | null;
  stageLabel?: string | null;
  stageItems: DealStageItem[];
  tags: PillData[];
  onTagsClick: () => void;
  kpiCards: DealSummaryKpiCard[];
  activeTab: string;
  onTabChange: (value: string) => void;
  onBack: () => void;
  quickActionsMenuOptionsList: ProfileMenuItemType[];
  showQuickActionsMenu: boolean;
  setShowQuickActionsMenu: Dispatch<SetStateAction<boolean>>;
}

export const DealSummaryHeaderSection = ({
  title,
  isLoading,
  pipelineTypeLabel,
  statusLabel,
  stageLabel,
  stageItems,
  tags,
  onTagsClick,
  kpiCards,
  activeTab,
  onTabChange,
  onBack,
  quickActionsMenuOptionsList,
  showQuickActionsMenu,
  setShowQuickActionsMenu,
}: DealSummaryHeaderSectionProps): JSX.Element => {
  return (
    <>
      <div className="DealSummaryBack">
        <Button
          text="Back"
          variant="secondary"
          icon={<ArrowFullLeftDarkIcon />}
          iconPosition="left"
          onClick={onBack}
          hasNoPadding
          minWidth={false}
        />
      </div>

      <div className="DealSummaryHeader">
        <h1 className="DealSummaryTitle">{title || (isLoading ? "Loading..." : "Deal summary")}</h1>
        <QuickActionsButton
          options={quickActionsMenuOptionsList}
          showMenu={showQuickActionsMenu}
          setShowMenu={setShowQuickActionsMenu}
        />
      </div>

      <div className="DealSummaryMeta">
        <div className="DealSummaryPills">
          <Pill label={pipelineTypeLabel ?? ""} variant="blue" />
          <Pill label={statusLabel ?? ""} variantMap={DEAL_STATUS_PILL_VARIANT_MAP} />
          <DealStagePill label={stageLabel ?? ""} stages={stageItems} />
        </div>
        <div className="DealSummaryTags">
          {tags.length > 0 ? (
            <ScrollablePills pills={tags} linkText="Tags" linkOnClick={onTagsClick} />
          ) : (
            <button type="button" className="DealSummaryTagsButton body2" onClick={onTagsClick}>
              + Add a tag
            </button>
          )}
        </div>
      </div>

      <div className="DealSummaryKpis">
        {kpiCards.map((card) => (
          <DefaultKPI
            key={card.key}
            className="DealSummaryKpiCard"
            header={card.label}
            value={card.value}
            iconLeft={card.icon}
          />
        ))}
      </div>

      <div className="DealSummaryTabs">
        <ToggleTabs
          toggleTabsList={[{ label: "Overview" }, { label: "Deal structure" }, { label: "Due diligence" }]}
          currentTab={activeTab}
          onChange={onTabChange}
          size="small"
          variant="roundedSquare"
        />
      </div>
    </>
  );
};
